import { useEffect, useMemo, useState } from 'react';
import { geoGraticule10, geoNaturalEarth1, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Topology } from 'topojson-specification';
import type { FeatureCollection } from 'geojson';

export interface MapPoint {
  id: string;
  lon: number;
  lat: number;
  /** on-air | planned | ready | warm | held | standby */
  kind: string;
  title: string;
}

let land: Promise<FeatureCollection> | null = null;
const loadLand = () =>
  (land ??= import('world-atlas/countries-110m.json').then((m) => {
    const topo = (m as { default?: unknown }).default ?? m;
    return feature(topo as Topology, (topo as Topology).objects.countries!) as unknown as FeatureCollection;
  }));

const W = 960;
const H = 470;

/** The camera pool on a flat world map: where the game is on air, what is planned next, what is held. */
export function WorldMap({ points, onPick, focus }: { points: MapPoint[]; onPick?: (id: string) => void; focus?: string | null }) {
  const [countries, setCountries] = useState<FeatureCollection | null>(null);
  useEffect(() => {
    let live = true;
    void loadLand().then((c) => live && setCountries(c));
    return () => {
      live = false;
    };
  }, []);
  const projection = useMemo(() => geoNaturalEarth1().scale(175).translate([W / 2, H / 2 + 20]), []);
  const path = useMemo(() => geoPath(projection), [projection]);
  const order = ['standby', 'held', 'ready', 'warm', 'planned', 'on-air'];
  const sorted = [...points].sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind));
  return (
    <svg className="world-map" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Camera pool map: ${points.length} cameras`}>
      <path d={path(geoGraticule10()) ?? ''} className="graticule" />
      {countries?.features.map((f, i) => <path key={i} d={path(f) ?? ''} className="land" />)}
      {sorted.map((p) => {
        const xy = projection([p.lon, p.lat]);
        if (!xy) return null;
        const r = p.kind === 'on-air' ? 7 : p.kind === 'planned' ? 6 : 4.5;
        return (
          <g key={p.id} transform={`translate(${xy[0].toFixed(1)} ${xy[1].toFixed(1)})`} className={`pt pt-${p.kind} ${focus === p.id ? 'focus' : ''}`} onClick={onPick ? () => onPick(p.id) : undefined}>
            {p.kind === 'on-air' && <circle r={16} className="ring" />}
            <circle r={r} />
            <title>{p.title}</title>
          </g>
        );
      })}
    </svg>
  );
}
